/**
 * qna.ts — the Rueckfragen / Q&A contract (P2 Placement Slice 9, Ausschreibungs-Screen Tab 3c,
 * Prozessschritt 8; Detailkonzept Ausschreibung V3.0 Ch 6.5 / 8.4 / 9 / 10 / 11, P2 §7 step 8,
 * §8 E3 + E16, §10.5, UC7).
 *
 * The SINGLE SOURCE OF TRUTH for:
 *   - the thread status vocabulary + the 10.5 status machine (offen <-> erledigt),
 *   - the message direction / channel / match-art / erledigt-art vocabularies,
 *   - the Antwortfrist arithmetic (reuses addArbeitstage from submission.ts) + the send-lock window,
 *   - the inbound NDR/bounce heuristic (pure, shared so api + tests agree),
 *   - the wire shapes (list / message / thread / submission eintrag + the action bodies),
 *   - the pure counters + verfasser helper.
 *
 * Pure module — no I/O, no formatters, no Date-TZ parse.
 *
 * [Source: docs/product/p2-slices/slice-9-qna-design.md §3]
 * [Source: packages/shared/src/submission.ts] (triad + Sperrgrund + wire idioms)
 */

import type { PlacementStatus } from './placement';
import type { SubmissionStatus } from './submission';

// ── Vocabularies (triad idiom) ───────────────────────────────────────────────

/** The lifecycle status of one Q&A thread (Ch 10.5). */
export const QNA_THREAD_STATUS = {
  OFFEN: 'offen',
  ERLEDIGT: 'erledigt',
} as const;
export type QnaThreadStatus = (typeof QNA_THREAD_STATUS)[keyof typeof QNA_THREAD_STATUS];
export const QNA_THREAD_STATUS_VALUES: readonly QnaThreadStatus[] = [
  QNA_THREAD_STATUS.OFFEN,
  QNA_THREAD_STATUS.ERLEDIGT,
];
export const QNA_THREAD_STATUS_LABELS: Record<QnaThreadStatus, string> = {
  [QNA_THREAD_STATUS.OFFEN]: 'offen',
  [QNA_THREAD_STATUS.ERLEDIGT]: 'erledigt',
};

/** The direction of one Q&A message (Ch 6.5). */
export const QNA_DIRECTION = {
  VERSICHERER_AN_BROKER: 'versicherer_an_broker',
  BROKER_AN_VERSICHERER: 'broker_an_versicherer',
} as const;
export type QnaDirection = (typeof QNA_DIRECTION)[keyof typeof QNA_DIRECTION];
export const QNA_DIRECTION_VALUES: readonly QnaDirection[] = [
  QNA_DIRECTION.VERSICHERER_AN_BROKER,
  QNA_DIRECTION.BROKER_AN_VERSICHERER,
];
export const QNA_DIRECTION_LABELS: Record<QnaDirection, string> = {
  [QNA_DIRECTION.VERSICHERER_AN_BROKER]: 'Versicherer an Broker',
  [QNA_DIRECTION.BROKER_AN_VERSICHERER]: 'Broker an Versicherer',
};

/** The channel a Q&A message travelled on (Ch 6.5 names only email). */
export const QNA_CHANNEL = {
  EMAIL: 'email',
} as const;
export type QnaChannel = (typeof QNA_CHANNEL)[keyof typeof QNA_CHANNEL];
export const QNA_CHANNEL_VALUES: readonly QnaChannel[] = [QNA_CHANNEL.EMAIL];
export const QNA_CHANNEL_LABELS: Record<QnaChannel, string> = {
  [QNA_CHANNEL.EMAIL]: 'E-Mail',
};

/** How an inbound carrier mail was matched to a submission (Ch 8.4). */
export const QNA_MATCH_ART = {
  THREAD: 'thread',
  BETREFF: 'betreff',
} as const;
export type QnaMatchArt = (typeof QNA_MATCH_ART)[keyof typeof QNA_MATCH_ART];
export const QNA_MATCH_ART_VALUES: readonly QnaMatchArt[] = [
  QNA_MATCH_ART.THREAD,
  QNA_MATCH_ART.BETREFF,
];
export const QNA_MATCH_ART_LABELS: Record<QnaMatchArt, string> = {
  [QNA_MATCH_ART.THREAD]: 'E-Mail-Verlauf',
  [QNA_MATCH_ART.BETREFF]: 'Submissions-Nummer im Betreff',
};

/** Why a thread was closed (Ch 10.5). */
export const QNA_ERLEDIGT_ART = {
  ANTWORT: 'antwort',
  MANUELL: 'manuell',
  KEINE_FRAGE: 'keine_frage',
} as const;
export type QnaErledigtArt = (typeof QNA_ERLEDIGT_ART)[keyof typeof QNA_ERLEDIGT_ART];
export const QNA_ERLEDIGT_ART_VALUES: readonly QnaErledigtArt[] = [
  QNA_ERLEDIGT_ART.ANTWORT,
  QNA_ERLEDIGT_ART.MANUELL,
  QNA_ERLEDIGT_ART.KEINE_FRAGE,
];
export const QNA_ERLEDIGT_ART_LABELS: Record<QnaErledigtArt, string> = {
  [QNA_ERLEDIGT_ART.ANTWORT]: 'beantwortet',
  [QNA_ERLEDIGT_ART.MANUELL]: 'manuell erledigt',
  [QNA_ERLEDIGT_ART.KEINE_FRAGE]: 'keine Rueckfrage',
};

// ── Status machine (10.5) ─────────────────────────────────────────────────────

/** The legal thread transitions: offen <-> erledigt. */
export const QNA_THREAD_STATUS_TRANSITIONS: Record<QnaThreadStatus, readonly QnaThreadStatus[]> = {
  offen: ['erledigt'],
  erledigt: ['offen'],
};

/** Is `to` a legal next status from `from`? The single guard for every thread transition. */
export function canTransitionQnaThread(from: QnaThreadStatus, to: QnaThreadStatus): boolean {
  return QNA_THREAD_STATUS_TRANSITIONS[from].includes(to);
}

/** The legal next statuses from `from` (a copy). */
export function qnaErlaubteTransitionen(from: QnaThreadStatus): QnaThreadStatus[] {
  return [...QNA_THREAD_STATUS_TRANSITIONS[from]];
}

// ── Frist (E3) ──────────────────────────────────────────────────────────────────

/** Default Antwortfrist in Arbeitstagen for a new/re-opened Rueckfrage (Q68). */
export const QNA_ANTWORT_FRIST_DEFAULT_ARBEITSTAGE = 3;
/** Upper bound for a manually set Frist (service 400 frist_invalid). */
export const QNA_ANTWORT_FRIST_MAX_TAGE = 60;
/** Minutes after which a versand_gestartet_at answer lock is stale and a re-send is allowed. */
export const QNA_VERSAND_LOCK_MINUTES = 10;
/** Max length of an answer text (service 400). */
export const QNA_TEXT_MAX = 20000;

/** Is the open thread overdue? `status === 'offen' && antwort_frist_date !== null && < today`. */
export function isQnaUeberfaellig(
  status: QnaThreadStatus,
  antwortFristDate: string | null,
  today: string,
): boolean {
  return status === 'offen' && antwortFristDate !== null && antwortFristDate < today;
}

// ── Bounce heuristic (E16, pure — shared so api + tests agree) ─────────────────────

/** Collapsed local-part markers that mark an NDR sender. */
export const QNA_BOUNCE_LOCAL_MARKERS: readonly string[] = ['mailerdaemon', 'postmaster'];

/** Subjects that mark a delivery failure (English + German, case-insensitive, prefix match). */
export const QNA_BOUNCE_SUBJECT_REGEX =
  /^(undeliverable|unzustellbar|nicht zustellbar|mail delivery failed|delivery status notification|delivery failure|undelivered mail|returned mail|fehler bei der zustellung)/i;

/**
 * A "Delivery Status Notification (Delay)" / "(Success)" is a status notice, NOT a delivery failure,
 * yet its subject starts with "delivery status notification" and so matches the regex above. Exclude
 * the (Delay)/(Success) variants so only genuine failures are treated as a bounce. ASCII only.
 */
export const QNA_BOUNCE_SUBJECT_EXCLUDE_REGEX = /\((delay|success)\)/i;

/** Collapse a local-part: lower-case, drop the plus-tag, strip non-alphanumerics (local copy). */
function collapseLocal(local: string): string {
  const beforePlusTag = local.toLowerCase().split('+', 1)[0] ?? '';
  return beforePlusTag.replace(/[^a-z0-9]/g, '');
}

/**
 * Is this inbound an NDR candidate? True when the collapsed local-part equals/startsWith a marker
 * (mailerdaemon/postmaster) OR the trimmed subject matches QNA_BOUNCE_SUBJECT_REGEX. Never throws.
 */
export function isBounceCandidate(localPart: string | null, subject: string | null): boolean {
  // A delay/success DSN is a status notice, never a failure - it is never a bounce, whatever the sender.
  if (typeof subject === 'string' && QNA_BOUNCE_SUBJECT_EXCLUDE_REGEX.test(subject.trim())) {
    return false;
  }
  if (localPart) {
    const collapsed = collapseLocal(localPart);
    if (collapsed && QNA_BOUNCE_LOCAL_MARKERS.some((m) => collapsed === m || collapsed.startsWith(m))) {
      return true;
    }
  }
  if (typeof subject === 'string' && subject.trim().length > 0) {
    return QNA_BOUNCE_SUBJECT_REGEX.test(subject.trim());
  }
  return false;
}

// ── Texts (ASCII hyphens only) ────────────────────────────────────────────────────

export const QNA_KEINE_RUECKFRAGEN_HINWEIS =
  'Noch keine Rueckfragen - eingehende E-Mails mit der Submissions-Nummer im Betreff erscheinen hier automatisch.';
export const QNA_NICHT_VERSANDT_HINWEIS =
  'Die Offertanfrage wurde noch nicht versandt - Rueckfragen sind erst danach moeglich.';
export const QNA_MAILBOX_HINWEIS =
  'Fuer die Antwort muss Ihr eigenes Postfach verbunden sein (Einstellungen - Postfach).';
export const QNA_UEBERFAELLIG_LABEL = 'Antwort ueberfaellig';
export const QNA_UNGELESEN_LABEL = 'ungelesen';
export const QNA_ABSENDER_ABWEICHEND_HINWEIS =
  'Absender weicht von der hinterlegten Versicherer-Adresse ab.';

// ── Events (Ch 11 verbatim + Slice-9 additions) ───────────────────────────────────

export const QNA_EVENTS = {
  QUESTION_RECEIVED: 'qna.question_received',
  QUESTION_ANSWERED: 'qna.question_answered',
  QUESTION_OVERDUE: 'qna.question_overdue',
  THREAD_CLOSED: 'qna.thread_closed',
  THREAD_REOPENED: 'qna.thread_reopened',
  ANSWER_BOUNCED: 'qna.answer_bounced',
} as const;
export type QnaEvent = (typeof QNA_EVENTS)[keyof typeof QNA_EVENTS];

// ── Wire types ──────────────────────────────────────────────────────────────────

export interface QnaMessageEintrag {
  id: string;
  direction: QnaDirection;
  channel: QnaChannel;
  communication_id: string;
  email_message_id: string | null;
  betreff: string | null;
  text: string;
  absender: string | null;
  empfaenger: string | null;
  attachment_count: number;
  match_art: QnaMatchArt | null;
  absender_abweichend: boolean;
  gelesen: boolean;
  gelesen_at: string | null;
  bounce_at: string | null;
  bounce_grund: string | null;
  /** outbound: app_user.display_name of created_by; inbound: absender display part. */
  verfasser: string | null;
  created_by: string | null;
  created_at: string;
}

export interface QnaThreadEintrag {
  id: string;
  status: QnaThreadStatus;
  status_label: string;
  status_changed_at: string;
  offen_seit: string | null;
  antwort_frist_date: string | null;
  ueberfaellig: boolean;
  frist_eskaliert_at: string | null;
  frist_task_id: string | null;
  frage_task_id: string | null;
  letzte_frage_at: string | null;
  letzte_antwort_at: string | null;
  anzahl_fragen: number;
  anzahl_antworten: number;
  ungelesen: number;
  erledigt_at: string | null;
  erledigt_by: string | null;
  erledigt_by_name: string | null;
  erledigt_art: QnaErledigtArt | null;
  erledigt_grund: string | null;
  versand_laeuft: boolean;
  letzter_versandfehler: string | null;
  letzter_versandfehler_at: string | null;
  erlaubte_transitionen: QnaThreadStatus[];
  /** #12: the address antworten() WILL send to (latest inbound wins, else the Versand mail). null = none resolvable. */
  antwort_empfaenger: string | null;
  /** domainOf(antwort_empfaenger) !== domainOf(carrier_email_to); a reply off the frozen carrier domain. */
  antwort_empfaenger_abweichend: boolean;
  antwort_empfaenger_quelle: 'letzte_eingehende' | 'versand' | null;
  /** created_at ASC. */
  messages: QnaMessageEintrag[];
  created_at: string;
  updated_at: string;
}

/** One per submission of the placement, Slice-8 order (created_at ASC). */
export interface QnaSubmissionEintrag {
  submission_id: string;
  submission_number: string;
  carrier_uid: string;
  name: string | null;
  logo_url: string | null;
  submission_status: SubmissionStatus;
  carrier_email_to: string | null;
  /** #12: lower-cased domain of carrier_email_to, exposed so the web does not need a second domain parser. */
  carrier_domain: string | null;
  /** status !== 'neu' — nothing was sent, so no Rueckfrage is possible yet. */
  antwort_moeglich: boolean;
  thread: QnaThreadEintrag | null;
}

export type QnaSperrgrund = 'placement_locked';

export interface QnaListResponse {
  placement_id: string;
  placement_number: string;
  status: PlacementStatus;
  /** closure offen. */
  aktion_erlaubt: boolean;
  sperrgrund: QnaSperrgrund | null;
  mailbox_verbunden: boolean;
  /** across all threads. */
  offen: number;
  ueberfaellig: number;
  ungelesen: number;
  /** addArbeitstage(today, 3) - what a new question would get. */
  frist_default_date: string;
  items: QnaSubmissionEintrag[];
}

export interface QnaAntwortenBody {
  /** 1..20000. */
  text: string;
  /** The off-domain address the broker confirmed; must equal the resolved recipient. */
  empfaenger_bestaetigt_an?: string;
}

export interface QnaErledigtBody {
  /** <= 2000. */
  grund?: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- reserved body, symmetric with the other action bodies
export interface QnaOffenBody {}

export interface QnaFristBody {
  /** ISO date (YYYY-MM-DD). */
  antwort_frist_date: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- reserved body, symmetric with the other action bodies
export interface QnaGelesenBody {}

export interface QnaGelesenResponse extends QnaListResponse {
  markiert: number;
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

/** The three list counters derived from the submission items' threads. */
export function qnaZaehler(items: readonly QnaSubmissionEintrag[]): {
  offen: number;
  ueberfaellig: number;
  ungelesen: number;
} {
  const z = { offen: 0, ueberfaellig: 0, ungelesen: 0 };
  for (const it of items) {
    const t = it.thread;
    if (!t) continue;
    if (t.status === 'offen') z.offen += 1;
    if (t.ueberfaellig) z.ueberfaellig += 1;
    z.ungelesen += t.ungelesen;
  }
  return z;
}

/** The display name of a sender header (part before `<`), else the address, else null. */
export function qnaVerfasserOf(absender: string | null): string | null {
  if (absender === null) return null;
  const trimmed = absender.trim();
  if (trimmed === '') return null;
  const lt = trimmed.lastIndexOf('<');
  if (lt > 0) {
    const name = trimmed.slice(0, lt).trim().replace(/^["']+|["']+$/g, '').trim();
    if (name !== '') return name;
  }
  const gt = trimmed.lastIndexOf('>');
  if (lt !== -1 && gt > lt) {
    const addr = trimmed.slice(lt + 1, gt).trim();
    if (addr !== '') return addr;
  }
  return trimmed;
}
